import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { incomeAPI, rankAPI } from '../api';

const Dashboard = () => {
  const { user } = useAuth();
  const [incomeSummary, setIncomeSummary] = useState(null);
  const [rankInfo, setRankInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, [user?._id]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [incomeRes, rankRes] = await Promise.all([
        incomeAPI.getIncomeSummary(),
        rankAPI.getMyRank(),
      ]);
      
      setIncomeSummary(incomeRes.data);
      setRankInfo(rankRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load dashboard data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><LoadingSpinner fullPage={false} /></div>;
  if (error) return <div className="flex items-center justify-center min-h-screen text-red-600">{error}</div>;

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">
      <div className="mb-10">
        <h1 className="text-4xl font-bold text-secondary mb-2">Welcome, {user?.name}!</h1>
        <p className="text-gray-500">Your Desi-Mart Dashboard</p>
      </div>

      {/* Main Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
        <div className="card bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-semibold mb-2">Current Rank</div>
          <div className="text-3xl font-bold text-primary mb-2">{incomeSummary?.rank}</div>
          <div className="text-xs text-gray-500">Business: Rs. {incomeSummary?.totalBusiness?.toLocaleString()}</div>
        </div>

        <div className="card bg-gradient-to-br from-primary to-primary-dark rounded-lg shadow-md p-6 text-white">
          <div className="text-white/90 text-sm font-semibold mb-2">Total Earned</div>
          <div className="text-3xl font-bold mb-2">Rs. {incomeSummary?.wallet?.totalEarned?.toLocaleString()}</div>
          <div className="text-xs text-white/80">Lifetime earnings</div>
        </div>

        <div className="card bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-semibold mb-2">Pending Amount</div>
          <div className="text-3xl font-bold text-warning mb-2">Rs. {incomeSummary?.wallet?.pendingAmount?.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Ready for payout</div>
        </div>

        <div className="card bg-white rounded-lg shadow-md p-6">
          <div className="text-gray-600 text-sm font-semibold mb-2">Total Payout</div>
          <div className="text-3xl font-bold text-accent mb-2">Rs. {incomeSummary?.wallet?.totalPayout?.toLocaleString()}</div>
          <div className="text-xs text-gray-500">Amount withdrawn</div>
        </div>
      </div>

      {/* Rank Progress */}
      {rankInfo && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-secondary mb-6">Rank Progress</h2>
          <div className="card bg-white rounded-lg shadow-md p-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center mb-6">
              <div className="text-center">
                <div className="text-sm text-gray-600 mb-2">Current Rank</div>
                <h3 className="text-2xl font-bold text-primary">{rankInfo.currentRank.rankName}</h3>
              </div>

              <div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                  <div 
                    className="bg-gradient-to-r from-primary to-primary-light h-3 rounded-full transition-all duration-500" 
                    style={{ width: `${rankInfo.progressPercentage}%` }}
                  ></div>
                </div>
                <div className="text-center text-sm text-gray-600">
                  {rankInfo.progressPercentage.toFixed(1)}% to next rank
                </div>
              </div>

              {rankInfo.nextRank ? (
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-2">Next Rank</div>
                  <h3 className="text-xl font-bold text-secondary mb-1">{rankInfo.nextRank.rankName}</h3>
                  {rankInfo.nextRank.rankName === 'DIRECTOR_CLUB' && (
                    <p className="text-xs text-red-600">Requires 40 direct sponsors</p>
                  )}
                  <p className="text-sm text-primary font-semibold">Rs. {rankInfo.businessToNextRank?.toLocaleString()} to go</p>
                </div>
              ) : (
                <div className="text-center">
                  <div className="text-sm text-gray-600 mb-2">Status</div>
                  <h3 className="text-xl font-bold text-accent">Maximum Achieved!</h3>
                  <p className="text-sm text-accent">You are at the top!</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Income Breakdown */}
      {incomeSummary?.incomeByType?.length > 0 && (
        <div className="mb-10">
          <h2 className="text-2xl font-bold text-secondary mb-6">Income Breakdown</h2>
          <div className="card bg-white rounded-lg shadow-md overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">Income Type</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-700">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {incomeSummary.incomeByType.map((income, idx) => (
                  <tr key={idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-secondary">{income._id?.replace(/_/g, ' ')}</td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-primary">Rs. {income.total?.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Monthly Income */}
      <div className="mb-10">
        <h2 className="text-2xl font-bold text-secondary mb-6">This Month</h2>
        <div className="card bg-gradient-to-br from-accent to-green-600 rounded-lg shadow-md p-8 text-white">
          <div className="text-white/90 text-sm font-semibold mb-2">Monthly Income</div>
          <div className="text-4xl font-bold">Rs. {incomeSummary?.monthlyIncome?.toLocaleString()}</div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
